// om-decode.ts — shared, optimized pure-TS decode pipeline for Open-Meteo .om
// chunks (faithful port of poc/om-decode.mjs). Bit-exact (see DECODER-NOTES.md
// and _validate.mjs).
//
// Pipeline per chunk (unchanged semantics):
//   1. raw first value: length-tagged SIGNED varint (see decodeChunk).
//   2. remaining N-1 values: p4dec128v16 (vertical bitunpack128v16 + scalar
//      bitmap/varbyte exceptions), each a zigzag value.
//   3. undo zigzag -> signed deltas, fused into:
//   4. undo 2-D second difference over the last two chunk dims (prefix sum
//      along last dim AND 2nd-last dim).
//   5. scatter chunk values (row-major within chunk) into the global array via
//      precomputed strides + incremental index walk (no per-element modulo).
//
// TS PORT NOTE: `buf` is a Uint8Array view of the whole file (browser fetch)
// rather than a Node Buffer; every access here is plain numeric indexing, so
// the logic is byte-for-byte identical to the .mjs source.

import { p4dec128v16 } from './p4codec.js'

// Reusable scratch buffers (grown on demand). Decoding is single-threaded /
// synchronous so sharing module-level scratch across chunks is safe.
let _tmp = new Uint16Array(0) // codec output (uint16 zigzag)
let _a = new Int32Array(0) // reconstructed chunk values (row-major)

function ensureScratch(n: number): void {
  if (_tmp.length < n) _tmp = new Uint16Array(n)
  if (_a.length < n) _a = new Int32Array(n)
}

// Metadata required to fully decode a variable into a flat array.
export interface ChunkMeta {
  dims: number[]
  chunk: number[]
  grid: number[]
  starts: number[] // length numChunks+1; chunk ci spans [starts[ci], starts[ci+1])
}

// Decode one chunk -> Int32Array of raw int values (row-major over cdims).
// Returns the shared _a buffer (valid until next call). `bytes` is the whole
// file buffer; `off` is the chunk's start byte offset (avoids per-chunk
// subarray allocation across thousands of chunks).
export function decodeChunk(bytes: Uint8Array, n: number, W: number, off = 0): Int32Array {
  ensureScratch(n)
  const tmp = _tmp
  const a = _a
  // raw first value — length-tagged SIGNED varint (SESSION 7). The top 2 bits of
  // the lead byte (top = b0>>6) select the encoded form; the low 6 bits are the
  // value's HIGH bits, with the follow bytes as little-endian LOW bytes:
  //   top 0 or 1 : single byte, value = b0            (0..127, non-negative)
  //   top == 2   : 1 follow byte,  value = (b0&0x3f)<<8  | b1            (UNSIGNED 14-bit)
  //   top == 3   : 2 follow bytes, value = (b0&0x3f)<<16 | b1 | b2<<8    (SIGNED int16,
  //                sign-extended from 16 bits) — this is the ONLY negative-capable form.
  const b0 = bytes[off]
  const top = b0 >> 6
  let first: number
  let p: number
  if (top <= 1) {
    first = b0 // 0..127, never negative
    p = off + 1
  } else if (top === 2) {
    first = (b0 & 0x3f) * 256 + bytes[off + 1] // unsigned 14-bit
    p = off + 2
  } else {
    // top === 3: signed int16
    first = (b0 & 0x3f) * 65536 + bytes[off + 1] + bytes[off + 2] * 256
    if (first & 0x8000) first -= 0x10000
    p = off + 3
  }

  // decode N-1 zigzag values via the p4 codec into tmp[0 .. n-2]
  let o = 0
  const m = n - 1
  while (o < m) {
    const cnt = m - o < 128 ? m - o : 128
    p += p4dec128v16(bytes, p, tmp, o, cnt, cnt === 128)
    o += cnt
  }

  // Fused un-zigzag + inverse 2-D second difference.
  // a[0] = first (raw); d[idx] = unzig(tmp[idx-1]) for idx>=1.
  a[0] = first
  // first row: idx in [1, W) -> 1-D prefix sum
  let prev = first
  const rowEnd = W < n ? W : n
  for (let idx = 1; idx < rowEnd; idx++) {
    const u = tmp[idx - 1]
    const d = u & 1 ? -((u + 1) >>> 1) : u >>> 1
    prev = d + prev
    a[idx] = prev
  }
  if (W < n) {
    // boundary seed at idx == W
    {
      const u = tmp[W - 1]
      const d = u & 1 ? -((u + 1) >>> 1) : u >>> 1
      a[W] = d + a[W - 1] + a[0]
    }
    // remaining: a[idx] = d + a[idx-1] + a[idx-W] - a[idx-W-1]
    for (let idx = W + 1; idx < n; idx++) {
      const u = tmp[idx - 1]
      const d = u & 1 ? -((u + 1) >>> 1) : u >>> 1
      a[idx] = d + a[idx - 1] + a[idx - W] - a[idx - W - 1]
    }
  }
  return a
}

// Windowed decode of a 2-D spatial variable ([nLat, nLon], chunk [ch, cw]).
// Decodes ONLY the chunks in the inclusive chunk-grid range [cr0..cr1]x[cc0..cc1]
// and scatters them into a window array sized to the chunk-aligned bounding box
// of that range (clipped to the grid). The window is itself row-major over
// (winRows x winCols); window cell (r,c) corresponds to global grid index
// (rowStart + r, colStart + c). Callers georeference the window as a standalone
// sub-grid (shifted bbox / projection origin), so no chunk in [starts] outside
// the range is touched and no full-size buffer is allocated.
export interface DecodedWindow {
  values: Int32Array
  winRows: number
  winCols: number
  rowStart: number // global array-row of window row 0
  colStart: number // global col of window col 0
}

export function windowDecode2D(
  meta: ChunkMeta,
  buf: Uint8Array,
  cr0: number,
  cr1: number,
  cc0: number,
  cc1: number,
  bufBase = 0
): DecodedWindow {
  if (meta.dims.length !== 2) {
    throw new Error(`windowDecode2D: expected 2-D variable, got ${meta.dims.length}-D`)
  }
  const nLat = meta.dims[0]
  const nLon = meta.dims[1]
  const ch = meta.chunk[0]
  const cw = meta.chunk[1]
  const gridCols = meta.grid[1]
  const starts = meta.starts

  const rowStart = cr0 * ch
  const colStart = cc0 * cw
  const rowEnd = Math.min((cr1 + 1) * ch, nLat)
  const colEnd = Math.min((cc1 + 1) * cw, nLon)
  const winRows = rowEnd - rowStart
  const winCols = colEnd - colStart
  const out = new Int32Array(winRows * winCols)

  for (let cr = cr0; cr <= cr1; cr++) {
    const originR = cr * ch
    const cdimsR = Math.min(ch, nLat - originR)
    const winRowOff = originR - rowStart
    for (let cc = cc0; cc <= cc1; cc++) {
      const originC = cc * cw
      const cdimsC = Math.min(cw, nLon - originC)
      const ci = cr * gridCols + cc
      const n = cdimsR * cdimsC
      const a = decodeChunk(buf, n, cdimsC, starts[ci] - bufBase)
      const winColOff = originC - colStart
      // scatter row-major chunk -> window sub-rectangle
      for (let i = 0; i < cdimsR; i++) {
        const src = i * cdimsC
        const dst = (winRowOff + i) * winCols + winColOff
        for (let j = 0; j < cdimsC; j++) out[dst + j] = a[src + j]
      }
    }
  }
  return { values: out, winRows, winCols, rowStart, colStart }
}

// Decode a SINGLE 2-D chunk `ci` into a fresh Int32Array (row-major over the
// chunk's clipped dims). `buf` may be a small span buffer rather than the whole
// file; `bufBase` is the absolute file offset of buf[0] so the absolute
// starts[ci] can be translated to a buffer-relative offset. The returned array
// is a copy (safe to cache); decodeChunk's shared scratch is not retained.
export interface ChunkTile {
  values: Int32Array // length cdimsR*cdimsC, row-major
  originR: number // global array-row of tile row 0
  originC: number // global col of tile col 0
  cdimsR: number
  cdimsC: number
}

export function decodeChunkTile(
  meta: ChunkMeta,
  buf: Uint8Array,
  ci: number,
  bufBase = 0
): ChunkTile {
  if (meta.dims.length !== 2) throw new Error('decodeChunkTile: expected 2-D variable')
  const nLat = meta.dims[0]
  const nLon = meta.dims[1]
  const ch = meta.chunk[0]
  const cw = meta.chunk[1]
  const gridCols = meta.grid[1]
  const cr = (ci / gridCols) | 0
  const cc = ci % gridCols
  const originR = cr * ch
  const originC = cc * cw
  const cdimsR = Math.min(ch, nLat - originR)
  const cdimsC = Math.min(cw, nLon - originC)
  const n = cdimsR * cdimsC
  const a = decodeChunk(buf, n, cdimsC, meta.starts[ci] - bufBase)
  return { values: Int32Array.from(a.subarray(0, n)), originR, originC, cdimsR, cdimsC }
}

// Assemble a window (chunk-aligned bounding box of the inclusive chunk range)
// from pre-decoded chunk tiles supplied by `getTile(ci)`. Window cell (r,c) maps
// to global grid index (rowStart + r, colStart + c).
export function assembleWindow(
  meta: ChunkMeta,
  getTile: (ci: number) => ChunkTile,
  cr0: number,
  cr1: number,
  cc0: number,
  cc1: number
): DecodedWindow {
  const nLat = meta.dims[0]
  const nLon = meta.dims[1]
  const ch = meta.chunk[0]
  const cw = meta.chunk[1]
  const gridCols = meta.grid[1]
  const rowStart = cr0 * ch
  const colStart = cc0 * cw
  const winRows = Math.min((cr1 + 1) * ch, nLat) - rowStart
  const winCols = Math.min((cc1 + 1) * cw, nLon) - colStart
  const out = new Int32Array(winRows * winCols)
  for (let cr = cr0; cr <= cr1; cr++) {
    for (let cc = cc0; cc <= cc1; cc++) {
      const t = getTile(cr * gridCols + cc)
      const winRowOff = t.originR - rowStart
      const winColOff = t.originC - colStart
      for (let i = 0; i < t.cdimsR; i++) {
        const src = i * t.cdimsC
        const dst = (winRowOff + i) * winCols + winColOff
        for (let j = 0; j < t.cdimsC; j++) out[dst + j] = t.values[src + j]
      }
    }
  }
  return { values: out, winRows, winCols, rowStart, colStart }
}

// Precompute the per-dimension global strides for an array of `dims`.
// global linear index = sum_d coord[d] * gstride[d].
function globalStrides(dims: Int32Array): Int32Array {
  const ndim = dims.length
  const gs = new Int32Array(ndim)
  let s = 1
  for (let d = ndim - 1; d >= 0; d--) {
    gs[d] = s
    s *= dims[d]
  }
  return gs
}

// Full decode of a variable into a flat Int32Array (row-major over dims).
export function fullDecode(meta: ChunkMeta, buf: Uint8Array): Int32Array {
  const ndim = meta.dims.length
  // Hoist arrays into typed arrays for fast indexing in the hot loop.
  const dims = Int32Array.from(meta.dims)
  const chunk = Int32Array.from(meta.chunk)
  const grid = Int32Array.from(meta.grid)
  const starts = meta.starts
  let nC = 1
  let total = 1
  for (let d = 0; d < ndim; d++) {
    nC *= grid[d]
    total *= dims[d]
  }
  const out = new Int32Array(total)
  const gstride = globalStrides(dims)

  const gc = new Int32Array(ndim) // chunk-grid coords
  const cdims = new Int32Array(ndim) // clipped chunk dims
  const base = new Int32Array(ndim) // global coord of chunk origin per dim

  for (let ci = 0; ci < nC; ci++) {
    // chunk-grid coords (row-major over grid)
    let rem = ci
    for (let d = ndim - 1; d >= 0; d--) {
      const g = grid[d]
      gc[d] = rem % g
      rem = (rem / g) | 0
    }
    // clipped chunk dims + global origin coord
    let n = 1
    for (let d = 0; d < ndim; d++) {
      const origin = gc[d] * chunk[d]
      base[d] = origin
      const cd = dims[d] - origin
      const c = cd < chunk[d] ? cd : chunk[d]
      cdims[d] = c
      n *= c
    }
    const W = cdims[ndim - 1]
    const a = decodeChunk(buf, n, W, starts[ci])

    // Scatter row-major chunk -> global, using incremental nested loops.
    scatter(out, a, ndim, cdims, base, gstride)
  }
  return out
}

// Scatter `a` (row-major over cdims, length=prod(cdims)) into `out` at the
// global position given by `base` (chunk origin coord per dim). The innermost
// (last) dim is contiguous in BOTH chunk and global layout, so we copy a
// contiguous run per row and advance the global base incrementally.
function scatter(
  out: Int32Array,
  a: Int32Array,
  ndim: number,
  cdims: Int32Array,
  base: Int32Array,
  gstride: Int32Array
): void {
  // Global base index of the chunk origin.
  let g0 = 0
  for (let d = 0; d < ndim; d++) g0 += base[d] * gstride[d]
  const W = cdims[ndim - 1]
  const lastStride = gstride[ndim - 1]

  if (ndim === 1) {
    // single contiguous run
    if (lastStride === 1) {
      out.set(a.subarray(0, W), g0)
    } else {
      for (let i = 0; i < W; i++) out[g0 + i * lastStride] = a[i]
    }
    return
  }

  // Iterate the outer dims (0 .. ndim-2) via an odometer, tracking the global
  // base index of each row incrementally. li walks the chunk row-major.
  const odo = new Int32Array(ndim - 1) // current coord in outer dims
  let li = 0
  let rowBase = g0
  const contiguous = lastStride === 1
  for (;;) {
    // emit one row of W contiguous chunk values starting at li -> rowBase
    if (contiguous) {
      out.set(a.subarray(li, li + W), rowBase)
    } else {
      for (let i = 0; i < W; i++) out[rowBase + i * lastStride] = a[li + i]
    }
    li += W
    // advance odometer over outer dims (least-significant = ndim-2)
    let d = ndim - 2
    for (;;) {
      odo[d]++
      rowBase += gstride[d]
      if (odo[d] < cdims[d]) break
      rowBase -= cdims[d] * gstride[d] // wrap this dim back to 0
      odo[d] = 0
      d--
      if (d < 0) return // all rows emitted
    }
  }
}
